import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import SEO, { serviceSchema } from '../components/SEO';
import { useEmpezar } from '../components/EmpezarModal';
import {
  ArrowRight,
  Check,
  Upload,
  ScanFace,
  DollarSign,
  QrCode,
  BarChart3,
  Mail,
  Sliders,
  MapPin,
  Calendar,
  Camera,
  Sparkles,
  Zap,
  ImageIcon,
  Users,
  ChevronRight,
  LayoutDashboard,
  Wallet,
  TrendingUp,
  Trophy,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { phoneMockups, heroMosaic } from '../mock';
import { getLangFromPath, localizePath } from '../i18n';
import {
  PhotoGridMockup,
  EventsMockup,
  LiquidacionesMockup,
  MarketIntelMockup,
} from '../components/PhotographerMockups';

/* ============ Mini visual components ============ */

function UploadCard() {
  const { t } = useTranslation();
  return (
    <div className="relative w-full h-full min-h-[220px] rounded-2xl bg-gradient-to-br from-[#141420] to-[#0e0e14] border border-white/8 p-5 overflow-hidden">
      <div className="absolute inset-3 rounded-xl border-2 border-dashed border-violet-400/30 flex flex-col items-center justify-center gap-2">
        <div className="w-12 h-12 rounded-2xl bg-violet-500/15 border border-violet-400/30 flex items-center justify-center">
          <Upload className="w-6 h-6 text-violet-300" />
        </div>
        <div className="text-xs text-white/70 font-medium">{t('mockups.sellphotos.dragHint')}</div>
        <div className="text-[10px] text-white/40">{t('mockups.sellphotos.dragTypes')}</div>
      </div>
      {/* Floating file preview */}
      <div className="absolute top-4 right-4 w-24 rounded-lg bg-[#1a1a24] border border-white/10 p-2 shadow-xl shadow-black/40">
        <div className="w-full h-12 rounded overflow-hidden mb-1.5">
          <img src={phoneMockups.album2} alt="preview" className="w-full h-full object-cover" />
        </div>
        <div className="h-1 w-full rounded-full bg-white/10 overflow-hidden">
          <div className="h-full w-3/4 bg-gradient-to-r from-violet-400 to-fuchsia-400 rounded-full" />
        </div>
        <div className="text-[9px] text-white/50 mt-1">{t('mockups.sellphotos.uploading')}</div>
      </div>
    </div>
  );
}

function AIMatchCard() {
  const { t } = useTranslation();
  return (
    <div className="relative w-full h-full min-h-[220px] rounded-2xl bg-gradient-to-br from-[#1a1329] to-[#0f0f18] border border-violet-400/20 p-5 overflow-hidden">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 rounded-full bg-violet-500/20 blur-2xl" />
      <div className="relative flex items-center justify-center gap-3 h-full">
        {/* Faces column */}
        <div className="flex flex-col gap-2">
          {[phoneMockups.album1, phoneMockups.album2, phoneMockups.album3].map((s, i) => (
            <div key={i} className="w-10 h-10 rounded-full overflow-hidden border-2 border-[#0a0a0f] shadow-lg">
              <img src={s} alt="face" className="w-full h-full object-cover" />
            </div>
          ))}
        </div>
        {/* Scanning core */}
        <div className="relative w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-400 to-fuchsia-500 flex items-center justify-center shadow-2xl shadow-violet-500/50">
          <ScanFace className="w-7 h-7 text-white" />
          <div className="absolute inset-0 rounded-2xl border border-white/40 animate-pulse" />
        </div>
        {/* Album grid */}
        <div className="grid grid-cols-2 gap-1.5">
          {[phoneMockups.album4, phoneMockups.album5, phoneMockups.album6, phoneMockups.album1].map((s, i) => (
            <div key={i} className="w-10 h-10 rounded-lg overflow-hidden border border-white/10">
              <img src={s} alt="match" className="w-full h-full object-cover" />
            </div>
          ))}
        </div>
      </div>
      <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-[10px] text-emerald-300">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
          {t('mockups.sellphotos.facesDetected')}
        </div>
        <div className="text-[10px] text-white/50">{t('mockups.sellphotos.accuracy')}</div>
      </div>
    </div>
  );
}

function SellCard() {
  const { t } = useTranslation();
  return (
    <div className="relative w-full h-full min-h-[220px] rounded-2xl bg-gradient-to-br from-[#141420] to-[#0e0e14] border border-white/8 p-5 overflow-hidden">
      <div className="relative w-28 h-36 mx-auto rounded-xl overflow-hidden border border-white/10 shadow-2xl shadow-black/50">
        <img src={phoneMockups.personA} alt="foto" className="w-full h-full object-cover" />
        <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-black/60 backdrop-blur-md text-[9px] text-white font-semibold">
          #IMG-024
        </div>
      </div>
      {/* Price tag */}
      <div className="absolute top-6 right-4 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white text-black text-xs font-bold shadow-xl">
        <DollarSign className="w-3 h-3" />
        4.90
      </div>
      {/* Buy button */}
      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-1.5 px-4 py-2 rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white text-xs font-semibold shadow-xl shadow-fuchsia-500/40 whitespace-nowrap">
        <Zap className="w-3.5 h-3.5" /> {t('mockups.sellphotos.soldIn3s')}
      </div>
      {/* Coin */}
      <div className="absolute bottom-16 right-6 w-10 h-10 rounded-full bg-gradient-to-br from-amber-300 to-amber-500 flex items-center justify-center text-white font-bold shadow-lg shadow-amber-500/40 rotate-12">
        $
      </div>
    </div>
  );
}

/* ---- Feature cards (small) ---- */

function QRVisual() {
  const { t } = useTranslation();
  return (
    <div className="relative flex items-center gap-4">
      <div className="w-20 h-20 rounded-xl bg-white p-1.5 shadow-lg shadow-black/30">
        <div className="grid grid-cols-6 gap-[2px] w-full h-full">
          {Array.from({ length: 36 }).map((_, i) => {
            const pattern = [0, 1, 2, 3, 4, 5, 6, 8, 11, 12, 15, 17, 18, 20, 23, 24, 26, 27, 30, 31, 32, 33, 34, 35];
            return (
              <div key={i} className={pattern.includes(i) ? 'bg-black rounded-[1px]' : 'bg-white'} />
            );
          })}
        </div>
      </div>
      <div className="flex-1">
        <div className="text-[10px] uppercase tracking-wider text-violet-300 mb-1">{t('mockups.sellphotos.uniqueLink')}</div>
        <div className="text-[11px] text-white/70 leading-snug">momentia.shop/juanperez</div>
      </div>
    </div>
  );
}

function PricingSliderVisual() {
  const { t } = useTranslation();
  return (
    <div className="relative">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[11px] text-white/70">{t('mockups.sellphotos.pricePerPhoto')}</div>
        <div className="text-sm font-display font-bold text-white">$ 4.90</div>
      </div>
      <div className="relative h-2 rounded-full bg-white/10 overflow-hidden">
        <div className="absolute inset-y-0 left-0 w-[62%] bg-gradient-to-r from-violet-400 to-fuchsia-400 rounded-full" />
        <div className="absolute top-1/2 -translate-y-1/2 left-[60%] w-4 h-4 rounded-full bg-white border-2 border-violet-400 shadow-md" />
      </div>
      <div className="mt-4 flex gap-2">
        <div className="flex-1 px-2 py-1.5 rounded-lg bg-white/5 border border-white/10 text-center">
          <div className="text-[9px] text-white/50">{t('mockups.sellphotos.pack5')}</div>
          <div className="text-[11px] font-semibold text-white">$ 19</div>
        </div>
        <div className="flex-1 px-2 py-1.5 rounded-lg bg-violet-500/15 border border-violet-400/30 text-center">
          <div className="text-[9px] text-violet-300">{t('mockups.sellphotos.all')}</div>
          <div className="text-[11px] font-semibold text-white">$ 39</div>
        </div>
      </div>
    </div>
  );
}

function AnalyticsVisual() {
  const bars = [30, 55, 45, 70, 60, 85, 75];
  return (
    <div>
      <div className="flex items-end justify-between gap-1.5 h-16">
        {bars.map((h, i) => (
          <div key={i} className="flex-1 rounded-t bg-gradient-to-t from-violet-500/40 to-fuchsia-400 relative" style={{ height: `${h}%` }}>
            {i === bars.length - 1 && (
              <div className="absolute -top-6 left-1/2 -translate-x-1/2 px-1.5 py-0.5 rounded bg-white text-black text-[9px] font-bold shadow whitespace-nowrap">+42%</div>
            )}
          </div>
        ))}
      </div>
      <div className="mt-3 flex items-center justify-between text-[10px] text-white/50">
        <span>L</span><span>M</span><span>M</span><span>J</span><span>V</span><span>S</span><span>D</span>
      </div>
    </div>
  );
}

function AlbumShareVisual() {
  return (
    <div className="relative h-24 flex items-center justify-center">
      <div className="w-16 h-16 rounded-xl overflow-hidden border-2 border-[#0a0a0f] shadow-xl z-10">
        <img src={phoneMockups.album2} alt="share" className="w-full h-full object-cover" />
      </div>
      {[{ x: -60, delay: 0, src: phoneMockups.album3 }, { x: 60, delay: 0.1, src: phoneMockups.album4 }, { x: -30, delay: 0.2, y: -30, src: phoneMockups.album5 }, { x: 30, delay: 0.3, y: -30, src: phoneMockups.album6 }].map((s, i) => (
        <div
          key={i}
          className="absolute w-10 h-10 rounded-full overflow-hidden border-2 border-[#0a0a0f] shadow-lg"
          style={{ transform: `translate(${s.x}px, ${s.y || 20}px)` }}
        >
          <img src={s.src} alt="friend" className="w-full h-full object-cover" />
        </div>
      ))}
    </div>
  );
}

function AutopilotVisual() {
  const { t } = useTranslation();
  const items = t('sellPhotos.features.autopilot', { returnObjects: true }) || [];
  const icons = [Mail, Sparkles, DollarSign];
  return (
    <div className="space-y-2">
      {items.map((m, i) => {
        const Icon = icons[i];
        return (
          <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-white/[0.03] border border-white/8">
            <div className="w-7 h-7 rounded-lg bg-violet-500/15 border border-violet-400/25 flex items-center justify-center text-violet-300 shrink-0">
              <Icon className="w-3.5 h-3.5" />
            </div>
            <div className="text-[11px] text-white/80 flex-1 truncate">{m.text}</div>
            <div className="text-[9px] text-white/40 shrink-0">{m.time}</div>
          </div>
        );
      })}
    </div>
  );
}

/* ============ Constants (icons only — copy comes from i18n) ============ */

const featureIcons = [QrCode, Sliders, ScanFace, BarChart3, Mail, Users];
const featureVisuals = [QRVisual, PricingSliderVisual, null, AnalyticsVisual, AutopilotVisual, AlbumShareVisual];
const eventTypeIcons = [Trophy, Calendar, Camera, MapPin];
const dashboardTabIcons = [ImageIcon, Trophy, Wallet, TrendingUp];
const dashboardTabComponents = [PhotoGridMockup, EventsMockup, LiquidacionesMockup, MarketIntelMockup];

/* Faces visual (was inline in features array) */
function FacesVisual() {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-3">
      <div className="flex -space-x-2">
        {[phoneMockups.album1, phoneMockups.album2, phoneMockups.album3, phoneMockups.album4].map((s, i) => (
          <div key={i} className="w-8 h-8 rounded-full overflow-hidden border-2 border-[#111116]">
            <img src={s} alt="" className="w-full h-full object-cover" />
          </div>
        ))}
      </div>
      <div className="text-[10px] text-white/60">{t('sellPhotos.features.uniqueFaces')}</div>
    </div>
  );
}

/* ============ Page ============ */

export default function SellPhotos() {
  const { t } = useTranslation();
  const location = useLocation();
  const lang = getLangFromPath(location.pathname);
  const to = (p) => localizePath(p, lang);
  const commissionItems = t('sellPhotos.commissions.items', { returnObjects: true }) || [];
  const featureItems = t('sellPhotos.features.items', { returnObjects: true }) || [];
  const eventItems = t('sellPhotos.anyEvent.items', { returnObjects: true }) || [];
  const pipelineItems = t('sellPhotos.pipeline.items', { returnObjects: true }) || [];
  const howItWorksSteps = t('sellPhotos.howItWorks.steps', { returnObjects: true }) || [];
  const dashboardTabsData = t('sellPhotos.dashboard.tabs', { returnObjects: true }) || [];
  const dashboardTabs = dashboardTabsData.map((tab, i) => ({
    ...tab,
    icon: dashboardTabIcons[i],
    Component: dashboardTabComponents[i],
  }));
  const [activeTab, setActiveTab] = useState('ventas');
  const { open: openEmpezar } = useEmpezar();
  const active = dashboardTabs.find((tab) => tab.id === activeTab) || dashboardTabs[0];
  const ActiveComponent = active.Component;
  return (
    <>
      <SEO
        title={t('sellPhotos.seoTitle')}
        description={t('sellPhotos.seoDescription')}
        path="/vende-tus-fotos"
        schema={serviceSchema({ name: 'Plataforma para Fotógrafos: Vender Fotos con IA', description: 'Solución para fotógrafos que quieren vender sus fotos por evento con reconocimiento facial y cobro automático.', path: '/vende-tus-fotos' })}
      />
      {/* HERO */}
      <section className="relative overflow-hidden pt-16 lg:pt-24 pb-16">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 -left-32 w-[500px] h-[500px] bg-violet-600/15 rounded-full blur-[120px]" />
          <div className="absolute -bottom-40 -right-40 w-[600px] h-[600px] bg-fuchsia-500/10 rounded-full blur-[140px]" />
        </div>
        <div className="container-mi relative">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-violet-400/25 bg-violet-500/10 text-xs font-medium text-violet-200 mb-5">
                <Camera className="w-3 h-3" /> {t('sellPhotos.hero.eyebrow')}
              </div>
              <h1 className="font-display text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.02]">
                {t('sellPhotos.hero.titleLine1')}
                <span className="block bg-gradient-to-r from-violet-300 via-violet-400 to-fuchsia-300 bg-clip-text text-transparent">
                  {t('sellPhotos.hero.titleHighlight')}
                </span>
              </h1>
              <p className="mt-6 text-lg md:text-xl text-white/70 max-w-2xl leading-relaxed">
                {t('sellPhotos.hero.subtitle')}
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={openEmpezar}
                  data-testid="sell-hero-empezar"
                  className="bg-white text-[#0a0a0f] hover:bg-white/90 h-12 px-6 rounded-full font-semibold"
                >
                  {t('sellPhotos.hero.ctaStart')} <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
                <a
                  href="https://link.sendlink.co/qr/cYpWQSUgvlsD"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="sell-hero-whatsapp"
                >
                  <Button variant="outline" className="h-12 px-6 rounded-full border-white/15 bg-white/5 hover:bg-white/10 text-white">
                    {t('sellPhotos.hero.ctaSales')}
                  </Button>
                </a>
              </div>
            </div>

            {/* Hero right visual - 3 mini cards preview */}
            <div className="lg:col-span-5 grid grid-cols-2 gap-3 relative">
              <div className="col-span-2 relative aspect-[16/9] rounded-2xl overflow-hidden border border-white/10">
                <img src={heroMosaic.left[0].src} alt="" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                <div className="absolute top-3 left-3 flex items-center gap-1.5 px-2 py-1 rounded-full bg-black/50 backdrop-blur-md border border-white/15 text-[10px] text-white">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Live
                </div>
                <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between">
                  <div>
                    <div className="text-[10px] text-white/60">{t('mockups.sellphotos.eventLabel')}</div>
                    <div className="text-sm font-semibold text-white">{t('mockups.sellphotos.eventName')}</div>
                  </div>
                  <div className="px-2.5 py-1 rounded-full bg-white text-black text-[10px] font-bold">+ USD 1.240</div>
                </div>
              </div>
              <div className="relative aspect-square rounded-2xl bg-[#111116] border border-white/8 p-3 flex flex-col justify-between">
                <div className="flex items-center justify-between">
                  <div className="w-8 h-8 rounded-lg bg-violet-500/15 border border-violet-400/25 flex items-center justify-center text-violet-300">
                    <QrCode className="w-4 h-4" />
                  </div>
                  <div className="text-[9px] text-white/40">{t('mockups.sellphotos.qrLabel')}</div>
                </div>
                <div>
                  <div className="text-[11px] text-white/70">{t('mockups.sellphotos.photosUploaded')}</div>
                  <div className="font-display text-2xl font-bold">1.284</div>
                </div>
              </div>
              <div className="relative aspect-square rounded-2xl bg-gradient-to-br from-violet-500/25 to-fuchsia-500/20 border border-violet-400/30 p-3 flex flex-col justify-between">
                <div className="flex items-center justify-between">
                  <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center text-white">
                    <DollarSign className="w-4 h-4" />
                  </div>
                  <div className="text-[9px] text-white/70">Hoy</div>
                </div>
                <div>
                  <div className="text-[11px] text-white/80">{t('mockups.sellphotos.cashout')}</div>
                  <div className="font-display text-2xl font-bold text-white">$ 340</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS — 3 illustrated cards */}
      <section className="py-20 lg:py-24 bg-[#08080c] border-y border-white/5">
        <div className="container-mi">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-violet-300 mb-3">{t('sellPhotos.howItWorks.eyebrow')}</div>
              <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight">{t('sellPhotos.howItWorks.title')}</h2>
            </div>
            <Link to={to('/precios')}>
              <Button variant="outline" className="border-white/15 bg-white/5 hover:bg-white/10 rounded-full">
                {t('sellPhotos.howItWorks.ctaPhotographers')} <ChevronRight className="ml-1 w-4 h-4" />
              </Button>
            </Link>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {howItWorksSteps.map((s, i) => {
              const visuals = [UploadCard, AIMatchCard, SellCard];
              const tones = ['default', 'violet', 'default'];
              const Visual = visuals[i];
              const tone = tones[i];
              return (
              <div
                key={i}
                className={`card-lift rounded-3xl p-6 border ${
                  tone === 'violet'
                    ? 'bg-gradient-to-br from-[#1a1329] to-[#0f0f18] border-violet-400/25 shadow-2xl shadow-violet-900/20'
                    : 'bg-[#111116] border-white/8'
                } flex flex-col`}
              >
                <div className="text-[10px] uppercase tracking-[0.2em] text-violet-300 mb-2">{s.step}</div>
                <h3 className="font-display text-2xl font-bold mb-3">{s.title}</h3>
                <p className="text-white/60 text-sm leading-relaxed mb-6">{s.desc}</p>
                <div className="mt-auto">
                  <Visual />
                </div>
              </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SOCIAL PROOF STAT */}
      <section className="py-16 lg:py-20">
        <div className="container-mi">
          <div className="relative overflow-hidden rounded-3xl border border-violet-400/20 bg-gradient-to-br from-[#1a1329] via-[#111116] to-[#0e0e14] p-8 md:p-12">
            <div className="absolute -top-24 -right-16 w-[400px] h-[400px] bg-violet-500/15 rounded-full blur-[100px]" />
            <div className="absolute -bottom-24 -left-16 w-[400px] h-[400px] bg-fuchsia-500/10 rounded-full blur-[100px]" />
            <div className="relative grid md:grid-cols-[auto_1fr] gap-8 md:gap-12 items-center">
              <div className="flex items-baseline gap-1">
                <span className="text-[3.5rem] md:text-[6rem] font-display font-black leading-none bg-gradient-to-b from-white via-violet-200 to-violet-500 bg-clip-text text-transparent">
                  $1.300
                </span>
                <span className="text-xl md:text-2xl font-display font-bold text-white/70">USD</span>
              </div>
              <div>
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-emerald-400/30 bg-emerald-500/10 text-xs font-medium text-emerald-300 mb-4">
                  <Sparkles className="w-3 h-3" /> {t('sellPhotos.socialProof.badge')}
                </div>
                <h3 className="font-display text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight leading-tight text-white">
                  {t('sellPhotos.socialProof.titleLine1')}
                  <span className="text-white/60"> {t('sellPhotos.socialProof.titleLine2')}</span>
                </h3>
                <p className="mt-4 text-white/60 leading-relaxed max-w-2xl">
                  {t('sellPhotos.socialProof.body')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ANY EVENT, ANYWHERE */}
      <section className="py-24 lg:py-28">
        <div className="container-mi">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-violet-300 mb-4">{t('sellPhotos.anyEvent.eyebrow')}</div>
              <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-tight">
                {t('sellPhotos.anyEvent.titleLine1')}
                <br />
                <span className="text-white/60">{t('sellPhotos.anyEvent.titleLine2')}</span>
              </h2>
              <p className="mt-5 text-lg text-white/70 leading-relaxed max-w-xl">
                {t('sellPhotos.anyEvent.description')}
              </p>
              <div className="mt-8 space-y-3">
                {eventItems.map((e, i) => {
                  const Icon = eventTypeIcons[i];
                  const featured = i === 0;
                  return (
                    <div
                      key={i}
                      className={`flex items-start gap-4 p-4 rounded-xl border card-lift ${
                        featured
                          ? 'bg-gradient-to-br from-violet-500/12 to-fuchsia-500/8 border-violet-400/30 relative overflow-hidden'
                          : 'bg-[#111116] border-white/8'
                      }`}
                    >
                      {featured && (
                        <div className="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-violet-500 text-[9px] font-semibold text-white uppercase tracking-wider shadow-lg shadow-violet-500/40">
                          {t('sellPhotos.anyEvent.exclusive')}
                        </div>
                      )}
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                        featured
                          ? 'bg-violet-500/25 border border-violet-400/40 text-violet-100'
                          : 'bg-violet-500/10 border border-violet-400/25 text-violet-300'
                      }`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-white">{e.title}</div>
                        <div className="text-sm text-white/60 mt-0.5">{e.desc}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Right visual: photo collage of scenarios */}
            <div className="grid grid-cols-2 gap-3 relative">
              <div className="space-y-3">
                <div className="aspect-[3/4] rounded-2xl overflow-hidden border border-white/10">
                  <img src={heroMosaic.left[0].src} alt="maratona" className="w-full h-full object-cover" />
                </div>
                <div className="aspect-[4/3] rounded-2xl overflow-hidden border border-white/10">
                  <img src={heroMosaic.left[1].src} alt="playa" className="w-full h-full object-cover" />
                </div>
              </div>
              <div className="space-y-3 pt-8">
                <div className="aspect-[4/3] rounded-2xl overflow-hidden border border-white/10">
                  <img src={heroMosaic.right[0].src} alt="graduación" className="w-full h-full object-cover" />
                </div>
                <div className="aspect-[3/4] rounded-2xl overflow-hidden border border-white/10">
                  <img src={heroMosaic.right[1].src} alt="estadio" className="w-full h-full object-cover" />
                </div>
              </div>
              <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 px-4 py-2 rounded-full bg-[#0a0a0f] border border-violet-400/30 shadow-xl shadow-violet-500/20 z-10">
                <Sparkles className="w-4 h-4 text-violet-300" />
                <span className="text-sm font-medium">{t('sellPhotos.anyEvent.floatingBadge')}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PHOTOGRAPHER DASHBOARD PREVIEW */}
      <section className="py-24 lg:py-28 bg-[#08080c] border-y border-white/5">
        <div className="container-mi">
          <div className="max-w-3xl mb-10">
            <div className="text-xs uppercase tracking-[0.2em] text-violet-300 mb-3 flex items-center gap-2">
              <LayoutDashboard className="w-3.5 h-3.5" /> {t('sellPhotos.dashboard.eyebrow')}
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-tight">
              {t('sellPhotos.dashboard.title')}
            </h2>
            <p className="mt-4 text-lg text-white/60 max-w-2xl">
              {t('sellPhotos.dashboard.subtitle')}
            </p>
          </div>

          {/* Tabs */}
          <div className="flex flex-wrap gap-2 mb-6">
            {dashboardTabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = tab.id === activeTab;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium transition-all ${
                    isActive
                      ? 'bg-violet-500 border-violet-400 text-white shadow-lg shadow-violet-500/30'
                      : 'bg-white/[0.03] border-white/10 text-white/70 hover:bg-white/[0.06] hover:text-white'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/* Mockup frame */}
          <div className="relative rounded-3xl bg-gradient-to-br from-[#141425] via-[#111116] to-[#0e0e14] border border-white/10 p-3 md:p-4 shadow-2xl shadow-black/60">
            <div className="absolute -top-24 -right-16 w-[400px] h-[400px] bg-violet-500/12 rounded-full blur-[120px] pointer-events-none" />
            <div className="absolute -bottom-24 -left-16 w-[400px] h-[400px] bg-fuchsia-500/10 rounded-full blur-[120px] pointer-events-none" />

            {/* Fake browser chrome */}
            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/8 mb-3">
              <div className="flex gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-red-400/70" />
                <div className="w-2.5 h-2.5 rounded-full bg-amber-400/70" />
                <div className="w-2.5 h-2.5 rounded-full bg-emerald-400/70" />
              </div>
              <div className="flex-1 mx-4">
                <div className="mx-auto max-w-md px-3 py-1 rounded-md bg-white/[0.04] border border-white/8 text-[10px] text-white/50 text-center">
                  momentia.shop/panel/fotografo
                </div>
              </div>
              <div className="text-[10px] text-white/40">v1.4</div>
            </div>

            <ActiveComponent />
          </div>

          <div className="mt-5 text-center text-sm text-white/60 max-w-2xl mx-auto">{active.caption}</div>
        </div>
      </section>

      {/* FEATURES GRID */}
      <section className="py-20 lg:py-24 bg-[#08080c] border-y border-white/5">
        <div className="container-mi">
          <div className="max-w-3xl mb-12">
            <div className="text-xs uppercase tracking-[0.2em] text-violet-300 mb-3">{t('sellPhotos.features.eyebrow')}</div>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-tight">
              {t('sellPhotos.features.title')}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {featureItems.map((f, i) => {
              const Icon = featureIcons[i];
              const Visual = featureVisuals[i] || FacesVisual;
              return (
                <div key={i} className="card-lift rounded-2xl bg-[#111116] border border-white/8 p-6 flex flex-col">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-9 h-9 rounded-lg bg-violet-500/10 border border-violet-400/25 flex items-center justify-center text-violet-300">
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-white/40">{f.tag}</div>
                  </div>
                  <h3 className="font-display text-lg font-semibold mb-2">{f.title}</h3>
                  <p className="text-white/60 text-sm leading-relaxed mb-5">{f.desc}</p>
                  <div className="mt-auto pt-4 border-t border-white/8">
                    <Visual />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PIPELINE */}
      <section className="py-24 lg:py-28">
        <div className="container-mi">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <div className="text-xs uppercase tracking-[0.2em] text-violet-300 mb-3">{t('sellPhotos.pipeline.eyebrow')}</div>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight">{t('sellPhotos.pipeline.title')}</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {pipelineItems.map((p, idx) => (
              <div key={p.step} className="relative">
                <div className="card-lift p-6 rounded-2xl bg-[#111116] border border-white/8 h-full">
                  <div className="text-violet-400 font-display font-semibold text-sm mb-3">{p.step}</div>
                  <h4 className="font-display font-semibold text-lg mb-2">{p.title}</h4>
                  <p className="text-white/60 text-sm leading-relaxed">{p.desc}</p>
                </div>
                {idx < pipelineItems.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-3 w-6 h-px bg-white/10" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMMISSIONS */}
      <section className="py-24 lg:py-28 bg-[#08080c] border-y border-white/5">
        <div className="container-mi">
          <div className="grid lg:grid-cols-2 gap-10 items-start">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-violet-300 mb-3">{t('sellPhotos.commissions.eyebrow')}</div>
              <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-tight">
                {t('sellPhotos.commissions.titleLine1')}
                <br />
                <span className="text-white/60">{t('sellPhotos.commissions.titleLine2')}</span>
              </h2>
              <p className="mt-5 text-white/70 leading-relaxed">
                {t('sellPhotos.commissions.description1')} <strong className="text-white">{t('sellPhotos.commissions.descriptionBold')}</strong>{t('sellPhotos.commissions.description2')}
              </p>
              <div className="mt-8">
                <Link to={to('/precios')}>
                  <Button className="bg-white text-[#0a0a0f] hover:bg-white/90 h-12 px-6 rounded-full font-semibold">
                    {t('sellPhotos.commissions.ctaPricing')} <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </div>
            <div className="rounded-3xl bg-[#111116] border border-white/8 p-8">
              <div className="space-y-3">
                {commissionItems.map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-violet-500/15 border border-violet-400/30 flex items-center justify-center shrink-0 mt-0.5">
                      <Check className="w-3.5 h-3.5 text-violet-300" />
                    </div>
                    <div className="text-white/80">{item}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="container-mi">
          <div className="relative overflow-hidden rounded-3xl border border-violet-400/20 bg-gradient-to-br from-[#141425] via-[#101018] to-[#0e0e14] p-10 md:p-14 text-center">
            <div className="absolute -top-32 -right-24 w-[500px] h-[500px] bg-violet-500/15 rounded-full blur-[100px]" />
            <div className="relative">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-violet-400/25 bg-violet-500/10 text-xs text-violet-200 mb-6">
                <ImageIcon className="w-3 h-3" /> {t('sellPhotos.finalCta.badge')}
              </div>
              <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight">
                {t('sellPhotos.finalCta.title')}
              </h2>
              <p className="mt-4 text-white/70 max-w-xl mx-auto">
                {t('sellPhotos.finalCta.subtitle')}
              </p>
              <div className="mt-8 flex justify-center gap-3 flex-wrap">
                <Button
                  onClick={openEmpezar}
                  data-testid="sell-cta-empezar"
                  className="bg-white text-[#0a0a0f] hover:bg-white/90 h-12 px-7 rounded-full font-semibold"
                >
                  {t('sellPhotos.finalCta.ctaStart')} <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
                <a
                  href="https://link.sendlink.co/qr/cYpWQSUgvlsD"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="sell-hero-whatsapp"
                >
                  <Button variant="outline" className="h-12 px-6 rounded-full border-white/15 bg-white/5 hover:bg-white/10 text-white">
                    {t('sellPhotos.finalCta.ctaSales')}
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
